import { z } from 'zod';
import { SUPPORT_CHANNELS } from '../constants/support-states.js';
import { usPhoneSchema, uuidSchema } from './common.js';
// New-support-ticket alerts (t1078): each staff member's channel opt-ins, the
// browsers they've allowed push on, and the lightweight feed the open portal
// polls to raise its in-page toast.
export const staffNotificationPrefsSchema = z.object({
    newTicketEmail: z.boolean(),
    newTicketSms: z.boolean(),
    /** Where text alerts go (E.164). Null until the person adds one. */
    newTicketSmsNumber: z.string().nullable(),
    newTicketPush: z.boolean(),
    workspaceDmPush: z.boolean(),
    workspaceDmEmail: z.boolean(),
    workspaceChannelPush: z.boolean(),
    taskCompletedEmail: z.boolean(),
    missedCallPush: z.boolean(),
    ticketNotePush: z.boolean(),
    taskPush: z.boolean(),
});
export const updateStaffNotificationPrefsSchema = z
    .object({
    newTicketEmail: z.boolean(),
    newTicketSms: z.boolean(),
    /** Loose human input in, E.164 out; null removes the number. */
    newTicketSmsNumber: usPhoneSchema.nullable(),
    newTicketPush: z.boolean(),
    workspaceDmPush: z.boolean(),
    workspaceDmEmail: z.boolean(),
    workspaceChannelPush: z.boolean(),
    taskCompletedEmail: z.boolean(),
    missedCallPush: z.boolean(),
    ticketNotePush: z.boolean(),
    taskPush: z.boolean(),
})
    .partial();
/** The shape `PushSubscription.toJSON()` produces in the browser. */
export const pushSubscriptionInputSchema = z.object({
    endpoint: z.url().max(2000),
    keys: z.object({
        p256dh: z.string().min(1).max(200),
        auth: z.string().min(1).max(100),
    }),
});
export const deletePushSubscriptionSchema = z.object({
    endpoint: z.url().max(2000),
});
export const vapidPublicKeyResponseSchema = z.object({ publicKey: z.string() });
export const supportTicketAlertsQuerySchema = z.object({
    since: z.iso.datetime(),
});
export const supportTicketAlertSchema = z.object({
    id: uuidSchema,
    ticketNumber: z.string(),
    subject: z.string(),
    channel: z.enum(SUPPORT_CHANNELS),
    createdAt: z.iso.datetime(),
});
export const supportTicketAlertsResponseSchema = z.object({
    /** Server clock at the time of the query — the client's next `since`, so a
     *  skewed browser clock can never skip or replay tickets. */
    serverTime: z.iso.datetime(),
    tickets: z.array(supportTicketAlertSchema),
});
// A staff member's own notification history (t1109): what they were alerted
// about, newest first, for the dashboard's Recent notifications card.
export const staffNotificationFeedItemSchema = z.object({
    id: uuidSchema,
    type: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    /** Admin-relative link to the thing the notification is about. */
    url: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const staffNotificationFeedQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const staffNotificationFeedResponseSchema = z.object({
    items: z.array(staffNotificationFeedItemSchema),
});
/** What the API puts in each web-push message and the service worker reads. */
export const newTicketPushPayloadSchema = z.object({
    type: z.literal('new-ticket'),
    ticketId: uuidSchema,
    ticketNumber: z.string(),
    subject: z.string(),
    channel: z.enum(SUPPORT_CHANNELS),
    url: z.string(),
});
/** Web-push payload for a new Workspace message (t1217). */
export const workspaceMessagePushPayloadSchema = z.object({
    type: z.literal('workspace-message'),
    spaceId: uuidSchema,
    spaceKind: z.enum(['dm', 'channel', 'board']),
    title: z.string(),
    body: z.string(),
    url: z.string(),
});
/** Web-push payload for the other dashboard-feed alerts (t1383): missed calls,
 *  notes on a ticket and task updates. Always shown as a banner. */
export const staffAlertPushPayloadSchema = z.object({
    type: z.literal('staff-alert'),
    kind: z.string(),
    title: z.string(),
    body: z.string(),
    url: z.string(),
    tag: z.string(),
});
//# sourceMappingURL=notifications.js.map